/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Nov 01 2024
 **/

import { useEffect, useState } from "react";
import { v4 } from "uuid";

import { AudioVisualizer } from "@/frameworks/libraries/media";

import styled from "@emotion/styled";
import { Icon, Row } from "@/frameworks/designStuff/components";

type MicVisualizer = {
	stream: MediaStream | undefined | null;
	list: MediaDeviceInfo[];
	isDisabled?: boolean;
};

function MicVisualizer({ stream, list = [], isDisabled = false }: MicVisualizer) {
	const [gainValue, setGainValue] = useState(0);

	useEffect(() => {
		let audioVisualizer: AudioVisualizer | null = null;
		if (!stream) return;
		if (list.length === 0) return;

		const hasAudioTracks = (stream.getAudioTracks() || []).length > 0;

		if (hasAudioTracks) {
			audioVisualizer = new AudioVisualizer(stream);
		}

		const interval = setInterval(() => {
			if (!audioVisualizer) return;
			setGainValue(audioVisualizer.volumeAverage);
		}, 100);

		return () => {
			audioVisualizer && audioVisualizer.disconnect();
			clearInterval(interval);
		};
	}, [stream]);

	return (
		<StyledVisualizer gap={8} horizonAlign="left" verticalAlign="center">
			<Icon name="MicOn24" size={16} />
			<Row.ul gap={4} horizonAlign="left">
				{Array.from({ length: 10 }, (_, i) => i + 1).map((item) => (
					<Gauge key={v4()} isDisabled={isDisabled} active={item <= Math.round((gainValue - 1) % 10) + 1} />
				))}
			</Row.ul>
		</StyledVisualizer>
	);
}

export default MicVisualizer;
const Gauge = styled(Row.li)<{ active: boolean; isDisabled?: boolean }>`
	width: 6px;
	height: 10px;
	border-radius: 20px;
	background: ${({ theme, active, isDisabled }) =>
		`${isDisabled ? theme.colors.neutral_30_100 : active ? theme.colors.accent_40_100 : theme.colors.neutral_70_100}`};
	transition: all 0.1 ease-in-out;
`;

const StyledVisualizer = styled(Row.div)`
	position: relative;
	padding: 8px 12px;
	display: flex;
	width: 100%;
	height: 40px;
	overflow: hidden;
	background: ${({ theme }) => theme.colors.neutral_50_8};
	align-items: center;
`;
